// Role-check helpers. Centralised here so adding a new role tier means
// editing one file, not chasing every component for `role === 'ADMIN'`.

import type { UserRole } from '@/types/database'

// Anything that the historical ADMIN gate covers — i.e. "trusted operator
// with full read+write across the app". SUPER_ADMIN is strictly above ADMIN
// and inherits everything ADMIN can do. FARM_ADMIN is the farm-app
// equivalent; the route gates (gFarm / gPack) confine each to its own
// tenant kind, so granting admin-tier authority is safe at the role level.
export function isAdmin(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'FARM_ADMIN'
}

// Strict super-admin check — only true for the SUPER_ADMIN tier. Reserved
// for destructive operations (deletes that can lose data permanently).
export function isSuperAdmin(role: UserRole | undefined | null): boolean {
  return role === 'SUPER_ADMIN'
}

// "Manager-ish" — covers ADMIN, SUPER_ADMIN, MANAGER, and their farm-app
// equivalents FARM_ADMIN + FARM_MANAGER. Used for the many ADMIN/MANAGER-
// or-above gates scattered through the app.
export function isManagerOrAbove(role: UserRole | undefined | null): boolean {
  return role === 'MANAGER'      || role === 'ADMIN'        || role === 'SUPER_ADMIN'
      || role === 'FARM_MANAGER' || role === 'FARM_ADMIN'
}

/** Who the chemicals INSERT policy admits (chemicals_write_insert): exactly
 *  ADMIN, MANAGER, FARM_ADMIN, FARM_MANAGER and FARM_FOREMAN, read from
 *  current_tenant_role(). Wider than isManagerOrAbove by the foreman, who may
 *  write the list; narrower by SUPER_ADMIN, whom the policy does not name —
 *  for them the database decides on their membership role in the tenant,
 *  which `me.role` does not carry. The spreadsheet import offers its button
 *  to exactly this set, so it never offers a write the database refuses. */
export function canWriteChemicals(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER'
      || role === 'FARM_ADMIN' || role === 'FARM_MANAGER' || role === 'FARM_FOREMAN'
}

/** Who the Estimates page's writes admit, one list per write, each read from
 *  that write's own server gate (U28 D-6). Pass the TENANT role, `tenantRoleOf(me)`,
 *  never `me.role`. A seatless SUPER_ADMIN is on none of the role lists.
 *  - lock_weekly_plan: ADMIN, MANAGER (tenant role).
 *  - pack_pct_overrides (insert/update/delete): current_tenant_role() ADMIN or
 *    MANAGER, plus tenant_id = current_tenant_id(). No SUPER_ADMIN and no
 *    platform arm: current_tenant_role() is NULL for a seatless SUPER_ADMIN.
 *  - block_estimate_weeks (weekly_write*) and blocks UPDATE (planting dates):
 *    ADMIN, MANAGER, FARM_ADMIN, FARM_MANAGER. (The blocks policy also admits
 *    DISPATCHER, mig 20261330000000; it is deliberately not offered here.)
 *  The platform-admin arm (is_platform_admin(), admitted by lock_weekly_plan and
 *  the blocks policy, NOT by the pack % or week-bin policies) is added by the
 *  caller: `platformView && !supportView`. */
export function canLockWeeklyPlanByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER'
}
export function canEditPackPctByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER'
}
export function canEditWeekBinsByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}
export function canEditPlantingDatesByRole(role: UserRole | undefined | null): boolean {
  return canEditWeekBinsByRole(role)
}

/** Who the logbook's RLS admits (vehicle_trips_write_insert / _write /
 *  _write_delete, mig 20260717075626): exactly ADMIN, MANAGER, FARM_ADMIN,
 *  FARM_MANAGER and FARM_FOREMAN. The foreman drives the bakkie and logs his
 *  own trips (FM08 UF-28). Wider than isManagerOrAbove by the foreman. */
export function canLogTrips(role: UserRole | undefined | null): boolean {
  return isManagerOrAbove(role) || role === 'FARM_FOREMAN'
}

/** Who may record a fuel DELIVERY or a DIP: the foreman who met the tanker or
 *  dipped the tank (FM08 UF-28). fuel_movements takes an insert from any
 *  member of the tenant (mig 603) and fuel_movements_modify admits managers
 *  and foremen. NOT tanks: adding, editing or deleting a tank stays
 *  isManagerOrAbove, as its RLS is. */
export function canRecordFuelMovements(role: UserRole | undefined | null): boolean {
  return isManagerOrAbove(role) || role === 'FARM_FOREMAN'
}

/** The tenant role RLS sees (current_tenant_role()), from useCurrentUser's
 *  profile. `me.role` is NOT it: SUPER_ADMIN stays sticky there. A snapshot
 *  written before `tenant_role` existed falls back to `role`, minus that
 *  sticky tier. (Moved here from modules/farm/blockEditGate.ts, which
 *  re-exports it, so shared code no longer imports a farm module for it.) */
export function tenantRoleOf(
  me: { role: UserRole; tenant_role?: UserRole | null } | null | undefined,
): UserRole | null {
  if (!me) return null
  if (me.tenant_role !== undefined) return me.tenant_role
  return me.role === 'SUPER_ADMIN' ? null : me.role
}

/** The seat rule delete_bin and delete_run share (mig 20261206000000): never in
 *  support view, and a live seat in the business (current_tenant_role() set) or
 *  platform view. The SUPER_ADMIN role check and any per-run rule stay with the caller. */
export function holdsHardDeleteSeat(c: {
  tenantRole: UserRole | null | undefined
  platformView: boolean
  supportView: boolean
}): boolean {
  if (c.supportView) return false
  return c.platformView || c.tenantRole != null
}

/** Who seed_phenology_stages admits by ROLE (mig 20261030300000): exactly
 *  ADMIN, MANAGER, FARM_ADMIN and FARM_MANAGER, read from current_tenant_role()
 *  — "Farm admins + managers" (Jason, 1 Oct 2026). Narrower than the
 *  phenology_stages write policies by the foreman, on purpose. Narrower than
 *  isManagerOrAbove by SUPER_ADMIN, whom the gate does not name.
 *
 *  Pass the TENANT role — `tenantRoleOf(me)` (above) —
 *  never `me.role`. `me.role` keeps a global SUPER_ADMIN whatever their seat
 *  here, and with no seat falls back to the global users.role; the RPC decides
 *  on the seat alone. This is only the role arm: the caller adds the gate's
 *  other two, in its order — a support view is refused, platform mode
 *  (is_platform_admin()) is admitted — as Phenology.tsx does. Kept apart from
 *  blockEditGate's REGISTRY_EDIT_ROLES, which happen to match today: this
 *  list names seed_phenology_stages' gate, so a registry policy change cannot
 *  silently move the preset button. */
export function canLoadPhenologyPreset(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who reprice_unpriced_chemical_uses admits by ROLE (mig 20261055700000, FM09
 *  RQ-3): exactly ADMIN, MANAGER, FARM_ADMIN and FARM_MANAGER, read from
 *  current_tenant_role(). It rewrites stock COSTS, so it is narrower than the
 *  chemical_movements UPDATE policy by the foreman and scale operator, and
 *  narrower than isManagerOrAbove by SUPER_ADMIN, whom the gate does not name.
 *  Pass the TENANT role — `tenantRoleOf(me)` — never `me.role`; the caller adds
 *  the support-view refusal. Platform mode is NOT admitted: the function is
 *  SECURITY INVOKER, so RLS would filter a platform admin with no seat to zero
 *  rows. */
export function canRepriceChemicalUses(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who may write a checklist run directly — exactly ADMIN, MANAGER,
 *  FARM_ADMIN and FARM_MANAGER, read from current_tenant_role(): that is what
 *  the UPDATE policy checklist_runs_tenant_write admits since mig
 *  20261081500000 (FM10 UF-13), and its role check is ANDed, so it binds
 *  platform admins too. Narrower than isManagerOrAbove by SUPER_ADMIN, whom the
 *  policy does not name, so a button behind the wider gate would show them a
 *  write the database turns down. The foreman and the worker stay out: they
 *  answer and complete only a run assigned to them, through the RPCs. */
export function canWriteChecklistRuns(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER'
      || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who reject_farm_load admits by ROLE (mig
 *  20261001084517, H88/L59): exactly MANAGER, ADMIN and SUPER_ADMIN, read from
 *  current_tenant_role(). Narrower than isManagerOrAbove by the FARM_ tiers,
 *  which the gate does not name. Pass the TENANT role — `tenantRoleOf(me)` —
 *  never `me.role`: a scale operator here who manages another business is
 *  refused. The gate's other arm, is_platform_admin(), is the caller's
 *  (platform mode admits; a support view mints no tenant role, so is refused). */
export function canAnswerFarmLoad(role: UserRole | undefined | null): boolean {
  return role === 'MANAGER' || role === 'ADMIN' || role === 'SUPER_ADMIN'
}

/** Who receive_farm_load admits by ROLE (mig 20261149000000, UF-10): the
 *  reject tier plus SCALE_OPERATOR, who is at the gate when the wagon arrives.
 *  Reject stays on `canAnswerFarmLoad`. Same rule: pass the TENANT role. */
export function canReceiveFarmLoad(role: UserRole | undefined | null): boolean {
  return canAnswerFarmLoad(role) || role === 'SCALE_OPERATOR'
}

/** The ROLE arm of block_fcm_biofix's write policies (insert / update /
 *  delete, mig 979 and 20260710200000): exactly ADMIN, MANAGER, FARM_ADMIN and
 *  FARM_MANAGER, read from current_tenant_role(). The policy's other arm is
 *  is_platform_admin(). Pass the TENANT role — `tenantRoleOf(me)` — never
 *  `me.role` (see canLoadPhenologyPreset). Kept apart from the lists above
 *  though they match today: this one names the biofix policy, so a phenology
 *  or payroll change cannot move Weather's "Override biofix" (FM06 UF-24). */
export function setsBiofixByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** The ROLE arm of payroll_periods_read / payroll_lines_read (and therefore of
 *  payroll_unpaid_hours, mig 20261032930000, which restates it): exactly ADMIN,
 *  MANAGER, FARM_ADMIN and FARM_MANAGER, read from current_tenant_role(). The
 *  policy's other two arms are can_manage_labour() and is_platform_admin();
 *  `useCanManageLabour()` carries both (platform mode makes `perms.all` true).
 *  Pass the TENANT role — `tenantRoleOf(me)` — never `me.role` (see above).
 *  Kept apart from canLoadPhenologyPreset though the list matches today: this
 *  one names the pay-run read policy, so a phenology change cannot move it. */
export function readsPayRunsByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who may add, edit, renew or delete a Compliance → Renewals item
 *  (compliance_items, mig 20261107500000, policies compliance_items_manager_insert
 *  and compliance_items_manager_update): exactly ADMIN, MANAGER, FARM_ADMIN and
 *  FARM_MANAGER from current_tenant_role(), or a platform admin. Reading is open to
 *  every member. Pass the TENANT role, `tenantRoleOf(me)`, never `me.role`. */
export function writesRenewalsByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who the packer performance page's reads admit by ROLE —
 *  get_worker_piece_trend (mig 20260807074108) and get_piece_performance
 *  (newest re-emit mig 20261022000003): exactly ADMIN, MANAGER, FARM_ADMIN and
 *  FARM_MANAGER from current_tenant_role(), or a platform admin. A tenant
 *  SUPER_ADMIN, a viewer and a custom labour-manager role are refused, so the
 *  register links a packer's name only for this set (P04 UF-15). Pass the
 *  TENANT role — `tenantRoleOf(me)` — never `me.role`. Kept apart from
 *  readsPayRunsByRole though the list matches: this names the performance
 *  reads, which UF-16 is due to widen. */
export function readsPiecePerformanceByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** Who end_piece_rate admits by ROLE (mig 20260707160000, its only body):
 *  exactly ADMIN, MANAGER, FARM_ADMIN and FARM_MANAGER from
 *  current_tenant_role(), or a platform admin. Narrower than set_piece_rate,
 *  which admits can_manage_labour() — so a custom labour-manager role may ADD
 *  or re-price a rate but not END one, nor move one to another unit/scope
 *  (that edit ends the old row). The rates page offers End and a scope move
 *  only to this set (F11 UF-12b). Pass the TENANT role — `tenantRoleOf(me)` —
 *  never `me.role`. Kept apart from the lists above though it matches: this
 *  names end_piece_rate's gate. */
export function endsPieceRatesByRole(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
}

/** The enum roles that hold `consignments:dispatch` by default (mig
 *  20260707084142: ADMIN, MANAGER, DISPATCHER) — the Stop ▾ / attach /
 *  detach gate on a mission's consignments. Pair with can('consignments',
 *  'dispatch') for custom roles. */
export function isDispatcherOrAbove(role: UserRole | undefined | null): boolean {
  return isManagerOrAbove(role) || role === 'DISPATCHER'
}

/** Who the send-report edge function lets email a report: its SEND_ROLES
 *  set is exactly ADMIN, MANAGER and FRANCHISE_ADMIN (platform admins pass
 *  separately). A UI gate wider than this shows a button whose send comes
 *  back "forbidden — role not permitted" — keep the two in lockstep. */
export function canEmailReports(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FRANCHISE_ADMIN'
}

// Growth-stage capture gate — the ENUM-role half. Scouts + foremen record
// stages in the field (they're the ones standing at the tree); the stage
// CATALOGUE and event DELETE stay manager-or-above. CORRECTING an event also
// admits anyone holding phenology edit — the FARM_FOREMAN role default does
// (mig 20260706150000), a scout's does not (FM06 ruling, 2 Oct). The DB side
// (upsert_phenology_event + block_phenology_events RLS) is tenant-scoped
// with no role predicate, so this client gate is the whole restriction —
// call sites pair it with usePermissions().can('phenology','edit') so
// custom roles holding that key aren't dead-ended on a read-only page.
export function canRecordPhenology(role: UserRole | undefined | null): boolean {
  return isManagerOrAbove(role) || role === 'FARM_SCOUT' || role === 'FARM_FOREMAN'
}

// Externally-packed pallet intake gate — the ENUM-role half. Mirrors the RLS
// the feature migration (20260807090000) deliberately wrote for the tables
// this screen writes: pallet_receipts_insert and runs_insert both name
// ADMIN / MANAGER / SCALE_OPERATOR, and the downstream strip-to-bin path is
// role-agnostic (pallet_lines, pallets_update) or names the role explicitly
// (bins_tenant_write). The weighbridge operator IS the person standing at the
// truck, so gating this on isManagerOrAbove alone handed the screen's primary
// user a read-only page while the database was ready to accept the write.
// Call sites pair it with usePermissions().can('deliveries','edit') so custom
// roles holding that key aren't dead-ended either.
export function canReceivePackedPallets(role: UserRole | undefined | null): boolean {
  return isManagerOrAbove(role) || role === 'SCALE_OPERATOR'
}

// Pallet admin gate — the ENUM-role half of the pallet destructive/override
// checks. Since mig 20260707070653 the server RPCs (delete_pallet,
// set_pallet_admin_overrides, _move_pallet_to_run_one, set_pallet_ssc) gate
// on has_perm('pallets','delete'|'override'), which admits ADMIN/SUPER_ADMIN
// via the built-in defaults AND custom roles holding those keys — so call
// sites pair this with usePermissions().can('pallets', …), e.g.
//   canAdminPallets(me?.role) || can('pallets','delete')
// Keeping the client gate in lockstep with the server avoids the surprise
// of a button that 403s on click.
export function canAdminPallets(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN'
}

/** Who the storage-billing guards admit (mig 20261033200000:
 *  pallet_receipts_billing_guard, runs_billing_guard, pallets_billing_guard,
 *  and change_receipt_ownership's own gate): a platform admin in platform
 *  view, or a member whose role in THIS business is exactly ADMIN or MANAGER.
 *  Narrower than isManagerOrAbove — a SUPER_ADMIN seat is refused (Jason,
 *  5 Oct evening: one rule). pallets_billing_guard's run_id arm also admits a
 *  seat holding pallets:override other than a SUPER_ADMIN seat (OVERRIDE,
 *  Jason 6 Oct; SANOOVR, 7 Oct) — Split's gate is canSplitReceipt; the payee
 *  and ownership locks stay exactly this set. */
export function canMoveStorageBilling(role: UserRole | undefined | null, inPlatformView: boolean): boolean {
  return inPlatformView || role === 'ADMIN' || role === 'MANAGER'
}

/** Who Split (the receipt's Another owner, split_receipt_pallets) admits on a
 *  received run — three server checks, mirrored:
 *  - its own gate, has_perm('pallets','override'): pass `holdsOverride` as
 *    `canAdminPallets(role) || can('pallets','override')`;
 *  - the receipt lock it takes (pallet_receipts_update RLS): a platform admin,
 *    or ADMIN / MANAGER / SCALE_OPERATOR of the business — so a SUPER_ADMIN
 *    seat, which holds the right by default, is still refused;
 *  - pallets_billing_guard's run_id arm (mig 20261033200000): ADMIN / MANAGER,
 *    a platform admin, or (OVERRIDE, Jason 6 Oct) a seat holding
 *    pallets:override — which the first check already demands — that is not
 *    a SUPER_ADMIN seat (SANOOVR, Jason 7 Oct: refused there too).
 *  So a custom role holding pallets:override on a scale-operator seat keeps
 *  Split (Jason, 6 Oct ~09:10). */
export function canSplitReceipt(role: UserRole | undefined | null, inPlatformView: boolean, holdsOverride: boolean): boolean {
  return holdsOverride && (canMoveStorageBilling(role, inPlatformView) || role === 'SCALE_OPERATOR')
}

/** Who the rate-card write policies admit — financial_products
 *  (fin_products_write_scoped / _insert / _delete) and its recipe lines, prod
 *  1 Oct 2026: a platform admin in platform view, or a member whose role in
 *  THIS business is exactly ADMIN. Narrower than isAdmin: current_tenant_role()
 *  returns SUPER_ADMIN or FARM_ADMIN as they are, and the policies name only
 *  'ADMIN'. A manager reads rate cards but every write is refused, so the
 *  forms are offered only to this set (ui-flow F19 D2).
 *
 *  Pass the SEAT role — `tenantRoleOf(me)` — never `me.role`: the policies read
 *  current_tenant_role(), and `me.role` keeps a global ADMIN whatever their
 *  seat here (RM9, owner ruling 25 Oct 2026). */
export function canWriteRateCards(seatRole: UserRole | undefined | null, inPlatformView: boolean): boolean {
  return inPlatformView || seatRole === 'ADMIN'
}

/** Who the rate-card READ policies admit — financial_products
 *  (fin_products_read_scoped) and product_recipes (fin_recipes_read), mig
 *  20261142500000 (RM9, owner ruling 25 Oct 2026): the role in THIS business
 *  decides, not users.role. A platform admin in platform view, a support view
 *  (a platform admin looking into the business; it mints no seat), or a member
 *  whose seat here is ADMIN, MANAGER or SUPER_ADMIN. FARM_* seats and floor
 *  roles are refused, whatever their global role. Mirrors the policy exactly:
 *  below it the read SUCCEEDS with no rows, so callers must not read at all. */
export function canReadRateCards(
  me: { role: UserRole; tenant_role?: UserRole | null } | null | undefined,
  ctx: { inPlatformView: boolean; inSupportView: boolean },
): boolean {
  if (ctx.inPlatformView || ctx.inSupportView) return true
  const seat = tenantRoleOf(me)
  return seat === 'ADMIN' || seat === 'MANAGER' || seat === 'SUPER_ADMIN'
}

/** Who the PPECB submit edge functions accept (SUBMIT_ROLES in
 *  supabase/functions/submit-ppecb-consignment/index.ts and
 *  submit-ppecb-addendum): SUPER_ADMIN, ADMIN, MANAGER, DISPATCHER, INSPECTOR.
 *  A QC technician holds the Inspection module but is refused, so the Send to
 *  PPECB submit button is offered only to this set (ui-flow F03 UF-15). Keep
 *  it in lockstep with that list. */
export function canSubmitPpecb(role: UserRole | undefined | null, platformBypass = false): boolean {
  return platformBypass || role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MANAGER'
      || role === 'DISPATCHER' || role === 'INSPECTOR'
}

/** The PPECB edge functions let a platform_admins member through whatever
 *  their role in the tenant (requireTenantRole and submit-ppecb-consignment
 *  both check the table directly). A SUPPORT VIEW is read-only in the app
 *  (the claim mints no tenant_role), so the bypass buttons stay hidden there.
 *  Pass the result as canSubmitPpecb / canAmendPpecb's second argument. */
export function ppecbPlatformBypass(isPlatformAdmin: boolean, inSupportView: boolean): boolean {
  return isPlatformAdmin && !inSupportView
}

/** Who may Amend & revalidate or Re-inspect (203): AMEND_ROLES in
 *  supabase/functions/update-ppecb-consignment/index.ts. Recall and delete
 *  stay with DELETE_ROLES (managers, isManagerOrAbove). Keep in lockstep
 *  (ui-flow F03 UF-16). */
export function canAmendPpecb(role: UserRole | undefined | null, platformBypass = false): boolean {
  return platformBypass || role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MANAGER'
      || role === 'DISPATCHER' || role === 'INSPECTOR'
}

/** The bar mig 20261066500000 sets for a reject bin's weights and stall
 *  takings (the reject_bins guard) and for a direct cage-movement edit (the
 *  mission_cage_movements update policy): a platform admin in platform view, a
 *  global SUPER_ADMIN (is_super_admin() — `me.role` keeps that tier sticky), or
 *  a member whose role in THIS business (current_tenant_role(), i.e.
 *  `me.tenant_role`) is ADMIN / SUPER_ADMIN / FARM_ADMIN / MANAGER — never in a
 *  support view. Reads `tenant_role`, not `role`: in a support view (and
 *  wherever there is no membership) `role` falls back to the GLOBAL users.role,
 *  so a staffer who is ADMIN somewhere else would be offered a write the
 *  database refuses. `tenant_role` undefined = an offline snapshot written
 *  before the field existed: fall back to `role`, as before. */
export function isTenantManagerOrAdmin(
  me: { role: UserRole; tenant_role?: UserRole | null } | null | undefined,
  scope: { inSupportView: boolean; inPlatformView: boolean },
): boolean {
  if (!me || scope.inSupportView) return false
  if (scope.inPlatformView || me.role === 'SUPER_ADMIN') return true
  const here = me.tenant_role === undefined ? me.role : me.tenant_role
  return isAdmin(here) || here === 'MANAGER'
}

/** Who may subscribe to an add-on — the Add-ons page's own rule (its
 *  Subscribe buttons and its "Ask an admin to subscribe" line): exactly ADMIN,
 *  SUPER_ADMIN and MANAGER, by base role. Subscribing is still a stub there
 *  (a platform admin enables an add-on via set_tenant_addon_status), so there
 *  is no server gate to mirror yet; the page is the rule. Home's header offers
 *  its Add-ons button to this set only (ui-flow F16 UF-17) — everyone else
 *  could only read "Ask an admin". One function, so the door and the page
 *  cannot disagree. Note it does NOT name FARM_ADMIN / FARM_MANAGER: the page
 *  never has, so neither does the door. */
export function canSubscribeAddons(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'MANAGER'
}

/** Whether Home's header offers "Work offline" (OfflinePrepButton). The prep
 *  warms the farm field screens and their lookups plus, on a packhouse, the
 *  piece-work station's device gate and quick-count tiles
 *  (buildFarmOfflineLookups) — so on a PACKHOUSE tenant a scale operator,
 *  loader, dispatcher, inspector or QC technician got nothing they open from
 *  it (ui-flow F16 UF-18, an approved removal). The approved card keeps it for
 *  admins, managers and anyone with Piece Work Capture, so the caller passes
 *  `isManagerOrAbove(role) || hasModule(…'packer_piecework'…)` — the role half
 *  matters: hasModule does not let an admin past a tenant that never switched
 *  Piece Work Capture on, nor a manager whose explicit module list omits it.
 *  Every other tenant kind — farms above all — keeps it for everyone, and so
 *  does a tenant whose kind is not known yet: never hide a farm's door on a
 *  slow read. */
export function offersOfflinePrep(tenantKind: string | null | undefined, managerOrPieceWork: boolean): boolean {
  return tenantKind !== 'PACKHOUSE' || managerOrPieceWork
}

/** Who may move a security incident to RESOLVED / FALSE_ALARM / CANCELLED by ROLE:
 *  the RESTRICTIVE policy security_incidents_close_is_manager (mig 20261021210000)
 *  admits ADMIN, MANAGER, SUPER_ADMIN, FARM_ADMIN and FARM_MANAGER from
 *  current_tenant_role() (and, from mig 20261128500000, SECURITY_CONTROLLER when
 *  current_tenant_kind() = 'SECURITY': pass the kind), or is_platform_admin() (the caller's: platform mode).
 *  A "manage" permission on a custom role does NOT count: the policy never asks
 *  has_perm. Includes SUPER_ADMIN, unlike the lists above. Pass the TENANT role. */
export function closesSecurityIncidentByRole(role: UserRole | undefined | null, tenantKind?: string | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'SUPER_ADMIN'
      || role === 'FARM_ADMIN' || role === 'FARM_MANAGER'
      // mig 20261128500000: a security company's controller closes too, in a SECURITY tenant only.
      || (role === 'SECURITY_CONTROLLER' && tenantKind === 'SECURITY')
}

/** Who the growers INSERT policy admits (growers_write_insert, verified on prod
 *  3 Nov 2026): ADMIN, MANAGER, FARM_ADMIN, FARM_MANAGER and SCALE_OPERATOR, read
 *  from current_tenant_role(). Pass the TENANT role. U26 UF-15: the delivery form
 *  offers "Add grower" to exactly this set, so the scale operator never meets a
 *  refusal. Wider than isManagerOrAbove by the scale operator; narrower by
 *  SUPER_ADMIN, whom the policy does not name. */
export function canAddGrowerFromIntake(tenantRole: UserRole | undefined | null): boolean {
  return tenantRole === 'ADMIN' || tenantRole === 'MANAGER'
      || tenantRole === 'FARM_ADMIN' || tenantRole === 'FARM_MANAGER' || tenantRole === 'SCALE_OPERATOR'
}
